"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useQuery } from "convex/react";
import { motion, useInView } from "motion/react";
import { ArrowRight } from "lucide-react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { Button } from "@/app/v2/_components/button/button";
import { formatDollarsShort, pluralize } from "@/lib/formatters";
import { Column } from "@/app/v2/_components/column/Column";
import { Text } from "@/app/v2/_components/text/Text";
import Counter from "@/components/animated-counter/Counter";

const ease = [0.16, 1, 0.3, 1] as const;

function CountUpOnView({
  target,
  duration = 1600,
  formatFn,
}: {
  target: number;
  duration?: number;
  formatFn?: (n: number) => string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const start = performance.now();
    let raf: number;
    const step = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      setValue(Math.round(target * (1 - Math.pow(1 - t, 4))));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [isInView, target, duration]);

  return <span ref={ref}>{formatFn ? formatFn(value) : value.toLocaleString()}</span>;
}

const fmt = formatDollarsShort;
const pl = pluralize;

export function BucketerResults({ runId, onDismiss }: { runId: Id<"bucketerRuns">; onDismiss?: () => void }) {
  const results = useQuery(api.bucketer.queries.getRunResults, { runId });

  const [animateCommission, setAnimateCommission] = useState(false);
  const [commission, setCommission] = useState(0);

  useEffect(() => {
    if (!results) return;
    const { run, buckets } = results;
    const bucketCommission = buckets.reduce((s, b) => s + (b.valueSummary?.commissionPotential ?? 0), 0);
    setCommission(Math.round(run.valueSummary?.commissionPotential ?? bucketCommission));
    setTimeout(() => setAnimateCommission(true), 1000);
  }, [results]);

  if (!results) {
    return (
      <section className="py-28 md:py-40">
        <div className="mx-auto max-w-4xl px-4 text-center text-foreground/30 text-sm">Loading results\u2026</div>
      </section>
    );
  }

  const { run, buckets } = results;
  // Fall back to summing bucket-level values when run valueSummary is missing
  const bucketTotalValue = buckets.reduce((s, b) => s + (b.valueSummary?.totalValue ?? 0), 0);
  //const bucketCommission = buckets.reduce((s, b) => s + (b.valueSummary?.commissionPotential ?? 0), 0);
  const totalValue = run.valueSummary?.totalPortfolioValue ?? bucketTotalValue;
  const turnover = (run.valueSummary?.turnoverRate ?? 0.02) * 100;
  const meta = run.metadata as Record<string, unknown> | undefined;
  const isExtrapolated = meta?.isExtrapolated === true;
  const scale = (meta?.scaleFactor as number) ?? 1;
  const totalCrm = (meta?.totalCrmContacts as number) ?? run.progress.totalContacts;

  const isCatchAll = (b: { rules?: unknown }) => !b.rules || (Array.isArray(b.rules) && b.rules.length === 0);

  const targeted = [...buckets]
    .filter((b) => b.contactCount > 0 && !isCatchAll(b))
    .sort((a, b) => (b.valueSummary?.totalValue ?? 0) - (a.valueSummary?.totalValue ?? 0));

  const catchAll = buckets.find((b) => isCatchAll(b) && b.contactCount > 0);
  const sampleSize = (meta?.sampleSize as number) ?? run.progress.totalContacts;
  const remainingContacts = Math.max(0, totalCrm - sampleSize);

  const topBucket = targeted[0];
  const restBuckets = targeted.slice(1);
  const maxVal = topBucket?.valueSummary?.totalValue ?? 1;

  return (
    <section>
      <div className="mx-auto max-w-7xl px-4 md:px-6 pb-24">
        {/* Hero — the money reveal */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="text-center mb-14"
        >
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease, delay: 0.4 }}
            className="text-2xl text-subdued-foreground max-w-2xl mx-auto font-bold"
          >
            With 5% turnover and 2.5% commission,
            <br />
            your database is worth
          </motion.p>

          <h2 className="text-6xl sm:text-7xl md:text-8xl lg:text-[7rem] font-extrabold text-emerald-500 leading-none tracking-tighter tabular-nums text-shadow-emerald-800 mt-4">
            {/* $<CountUpOnView target={commission} duration={2000} formatFn={(n) => n.toLocaleString()} /> */}
            {commission > 0 && (
              <Counter
                value={animateCommission ? commission : 0}
                places={Array.from(
                  { length: commission.toString().length },
                  (_, i) => 10 ** (commission.toString().length - i - 1),
                )}
                digitStyle={{ minWidth: "15px", width: "4.2rem" }}
                digitHeight={84}
                prefix="$"
                prefixClassName="pr-2"
                fontClassName="text-[7rem] font-black text-emerald-500"
                prefixStyle={{ color: "inherit", width: "fit-content" }}
              />
            )}
          </h2>

          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease, delay: 0.4 }}
            className="text-xl text-subdued-foreground mt-4 max-w-lg mx-auto"
          >
            in estimated annual commission potential
          </motion.p>

          {/* Sub-stats — quietly supporting */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="mt-12 inline-flex items-center gap-8 md:gap-12"
          >
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-bold text-foreground tabular-nums">{fmt(totalValue)}</p>
              <p className="text-[11px] text-foreground/35 mt-1">{isExtrapolated ? "Est. r" : "R"}eal estate value</p>
            </div>
            <div className="w-px h-10 bg-foreground/10" aria-hidden />
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-bold text-foreground tabular-nums">{turnover}%</p>
              <p className="text-[11px] text-foreground/35 mt-1">Annual turnover</p>
            </div>
            <div className="w-px h-10 bg-foreground/10" aria-hidden />
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-bold text-foreground tabular-nums">{targeted.length}</p>
              <p className="text-[11px] text-foreground/35 mt-1">Segments</p>
            </div>
          </motion.div>
        </motion.div>

        {/* Top opportunity — given special treatment */}
        {topBucket && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease, delay: 0.15 }}
            className="mb-4"
          >
            <div className="rounded-2xl border border-emerald-500 bg-linear-to-b from-white to-emerald-300/20 p-7 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[10px] uppercase tracking-[0.12em] font-bold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded">
                  Top Opportunity
                </span>
              </div>
              <div className="flex items-start justify-between gap-4 mb-2">
                <h3 className="text-xl font-bold text-foreground leading-snug">{topBucket.name}</h3>
                <span className="shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 whitespace-nowrap">
                  {isExtrapolated ? "~" : ""}
                  {(isExtrapolated
                    ? Math.round(topBucket.contactCount * scale)
                    : topBucket.contactCount
                  ).toLocaleString()}{" "}
                  {pl(isExtrapolated ? Math.round(topBucket.contactCount * scale) : topBucket.contactCount, "contact")}
                </span>
              </div>
              <p className="text-sm text-foreground/50 leading-relaxed mb-4">{topBucket.description}</p>
              <div className="w-full bg-emerald-100 rounded-full h-1 mb-4">
                <div className="h-1 rounded-full bg-emerald-500 w-full" />
              </div>
              <div className="flex items-center gap-6 text-sm">
                <span className="text-foreground/60">
                  <span className="font-bold text-foreground">{fmt(topBucket.valueSummary?.totalValue ?? 0)}</span>{" "}
                  portfolio
                </span>
                <span className="text-emerald-700/80">
                  <span className="font-bold text-emerald-700">
                    {fmt(Math.round(topBucket.valueSummary?.commissionPotential ?? 0))}
                  </span>{" "}
                  commission
                </span>
              </div>
            </div>
          </motion.div>
        )}

        {/* Remaining segments */}
        <div className="grid md:grid-cols-2 gap-3">
          {restBuckets.map((bucket, i) => {
            const estCount = isExtrapolated ? Math.round(bucket.contactCount * scale) : bucket.contactCount;
            const relVal = (bucket.valueSummary?.totalValue ?? 0) / maxVal;
            const barWidth = Math.max(relVal * 100, 4);

            return (
              <motion.div
                key={bucket._id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease, delay: 0.2 + i * 0.05 }}
              >
                <div className="rounded-xl border border-border bg-white shadow-xs hover:shadow-sm transition-shadow p-5 h-full flex flex-col">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="text-[15px] font-bold text-foreground leading-snug">{bucket.name}</h3>
                    <span className="shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium bg-foreground/[0.04] text-foreground/50 whitespace-nowrap">
                      {isExtrapolated ? "~" : ""}
                      {estCount.toLocaleString()} {pl(estCount, "contact")}
                    </span>
                  </div>
                  <p className="text-[13px] text-foreground/45 leading-relaxed mb-3 line-clamp-2 flex-1">
                    {bucket.description}
                  </p>
                  {(bucket.valueSummary?.totalValue ?? 0) > 0 && (
                    <div className="mb-3">
                      <div className="w-full bg-emerald-500/[0.08] rounded-full h-[3px]">
                        <div
                          className="h-[3px] rounded-full bg-emerald-500/40 transition-all duration-700"
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-4 text-[13px]">
                    <span className="text-foreground/50">
                      <span className="font-semibold text-foreground/70">
                        {fmt(bucket.valueSummary?.totalValue ?? 0)}
                      </span>{" "}
                      portfolio
                    </span>
                    <span className="text-emerald-700/70">
                      <span className="font-semibold text-emerald-700">
                        {fmt(Math.round(bucket.valueSummary?.commissionPotential ?? 0))}
                      </span>{" "}
                      commission
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Catch-all as opportunity footnote */}
        {isExtrapolated && remainingContacts > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.45 }}
            className="mt-6 rounded-xl border border-foreground/6 bg-foreground/1.5 px-5 py-4"
          >
            <Column tightGap>
              <Text sm fat>
                Important
              </Text>
              <p className="text-sm text-foreground/60 mt-0.5">
                The above data is based on a sample of {sampleSize.toLocaleString()} contacts from your database of{" "}
                {totalCrm.toLocaleString()}. You have{" "}
                <span className="font-semibold text-foreground/80">{remainingContacts.toLocaleString()}</span> remaining
                contacts that have not yet been enriched.
              </p>
            </Column>
          </motion.div>
        )}

        {/* CTA — the money button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease, delay: 0.5 }}
          className="text-center absolute bottom-0 left-0 right-0 py-12 pb-12 bg-linear-to-t from-gray-900/10 to-transparent"
        >
          <Column>
            {onDismiss ? (
              <Button
                variant="bold"
                size="2xl"
                className="text-xl rounded-full shadow-lg shadow-emerald-500/20 w-fit mx-auto"
                onClick={onDismiss}
              >
                See your full results
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            ) : (
              <Link href="/v2/activate">
                <Button variant="bold" className="shadow-lg shadow-emerald-500/20">
                  See your full results
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            )}
          </Column>
        </motion.div>
      </div>
    </section>
  );
}
